import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Spinner, Info, Hand, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsClockwise,
  PaintBrush, Eraser, Minus, Plus, Scissors, PaintBucket, FloppyDisk,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
// MICRO-STEP 3.8 — reuse the EXISTING Media Engine upload endpoint (POST
// /media, the exact same one Media.jsx already posts a FormData to) instead
// of inventing a second upload mechanism.
import api from "@/lib/api";
// MICRO-STEP 3.2 — reuse the EXACT SAME palette (same colors, same order) as
// the Background Editor's swatches ("Modifica sfondo", `ph-bg-swatch-*`)
// instead of defining a second, divergeable copy here.
import { BG_PALETTE } from "./colorPalette";

// MICRO-STEP 1 — Retouch Editor shell (A Caccia Prodotto → Libreria Prodotti →
// Modifica sfondo → "Ritocca immagine").
// MICRO-STEP 2 (+2.1..2.8) — Zoom + Pan navigation on top of the shell.
// MICRO-STEP 3 — Mask layer + Brush + Eraser on top of that.
// MICRO-STEP 3.1 — Local "Remove area" on the edit buffer.
// MICRO-STEP 3.2 — Shared color palette ("Colore", same swatches as the
// Background Editor) + a FOURTH offscreen layer, `paintLayerRef`, and
// redefines "Riempi" to use the selected color instead of 3.1's neighbor-pixel
// reconstruction (see the note above `handleFill` below for why).
// MICRO-STEP 3.4 — fixes the mask overlay VISUALLY tinting every paint color
// red/brown: the mask is now composited inside `draw()` itself, sandwiched
// between the edit buffer and the paint layer, instead of on a separate,
// topmost DOM canvas that sat on top of everything. See the comments on
// `draw()`/`drawMask()` below for the full before/after. No mask/paint DATA,
// coordinate math, zoom/pan, or tool logic changed — this is a display/
// compositing-order fix only.
// MICRO-STEP 3.5 — fixes "Riempi" repainting the ENTIRE masked area with the
// currently selected color, destroying any different color already there
// (from an earlier brush stroke or an earlier "Riempi" pass). It now composes
// the mask-clipped color patch onto the paint layer with `destination-over`
// instead of clearing the whole masked stencil first — see the comment right
// above the fix in `handleFill()` for the exact compositing algebra. Brush,
// Eraser, "Rimuovi area", zoom/pan/coordinates and the palette are all
// unchanged.
// MICRO-STEP 3.7 — fixes "Riempi" still completing an area with whatever
// color happens to be selected NOW instead of that area's OWN historical
// color, when several differently-colored strokes were painted before a
// single "Riempi" click. Adds `paintStrokesRef` (a small in-memory history of
// committed brush strokes — points/size/color, never Eraser) and replays each
// one's own footprint/color, mask-clipped, before the 3.5 current-color patch
// runs as a last-resort fallback. See the comments on `paintStrokesRef` and
// inside `handleFill()` for the full mechanics. Brush, Eraser, zoom/pan/
// coordinates, the mask/edit-buffer architecture and the palette are all
// unchanged.
// MICRO-STEP 3.8 — adds "Salva": exports EDIT BUFFER + PAINT LAYER (never the
// mask overlay/cursor/UI, never the viewport's current zoom/pan) as a PNG at
// image-space resolution, uploads it through the EXISTING, unmodified Media
// Engine (`POST /media`, same call Media.jsx already makes), then links that
// already-stored result to the product as a new derived asset via the new
// `POST /products/{id}/save-retouch-image` backend endpoint (mirrors
// /edit-image's asset-linking shape; own role, no AI/provider involved). The
// modal only closes and only calls `onSaved` after that round-trip actually
// succeeds; on any failure the modal stays open with the local edit fully
// intact and the error shown, so the user can retry without losing work. A
// ref-based guard (`savingRef`) makes a double click impossible to turn into
// two derived assets. See `handleSave` below for the full mechanics.
//
// Scope up to and including this step:
//   - open/close the modal
//   - draw the ORIGINAL image onto a <canvas>, sized to fit the modal
//   - zoom (wheel, +/- buttons, reset) and pan (space+drag or Pan tool)
//   - a second, transparent mask overlay canvas, always kept aligned to the
//     image at any zoom/pan/resize; Brush paints it, Eraser removes from it,
//     brush size is configurable (image-space px, unaffected by zoom)
//   - a THIRD offscreen buffer, `editBufferRef` — an image-space copy of the
//     original, and the bottom layer the visible canvas draws from (see
//     `draw()`). "Rimuovi area" punches transparent holes into this buffer
//     wherever the mask is painted (never touching the original `<img>`
//     element or `imageUrl`).
//   - a FOURTH offscreen buffer, `paintLayerRef` — also image-space, drawn on
//     TOP of `editBufferRef` in `draw()`. This is where actual color retouches
//     live: Brush strokes the currently selected palette color onto it (in
//     addition to still marking the mask, as before); Eraser removes from it
//     with the identical stroke geometry (never from `editBufferRef`, so it
//     can only ever undo a color retouch, not the original/removed-area
//     result underneath); "Riempi" paints the selected color into it, clipped
//     to the current mask.
//   - the mask, edit buffer and paint layer live ONLY in this component's
//     memory until "Salva" (MICRO-STEP 3.8) is clicked — no
//     selection/lasso/undo/redo, and no inpainting/provider/job of any kind
//     (Save uses the existing Media Engine + a small linking endpoint, no AI)
//
// The ORIGINAL is never mutated: `imgElRef` (the raw, loaded `Image`) is only
// ever read once, to (re)seed `editBufferRef` when a new asset loads; "Rimuovi
// area" is the only thing that writes to `editBufferRef` itself, and Brush/
// Eraser/"Riempi" (color retouching) only ever write to the separate
// `paintLayerRef` on top of it — so Eraser reveals `editBufferRef`
// (original, or original-with-holes), never anything destructive. MICRO-STEP
// 3.8: `onSaved(url)` is now actually invoked, but only once "Salva" has
// genuinely finished uploading + linking the result server-side — see the
// comment above `handleSave` below.
//
// NOTE on CORS: the image canvas here is only ever drawn to for display (the
// mask canvas is authored entirely from local pointer input, not from the
// image, so it is never tainted). A future step that needs to export the
// mask alongside actual image pixels (e.g. for server-side inpainting) will
// have to revisit whether the image response needs CORS headers — out of
// scope here.
//
// Coordinate spaces (kept deliberately explicit — this is what let Brush/
// Eraser reuse `viewportToImagePoint` as-is, unchanged, for painting):
//   - "image space": pixels of the (possibly downscaled, see MAX_CANVAS_DIM)
//     source image. This is the canonical space the mask layer (`maskDataRef`)
//     is authored in, independent of the current zoom/pan.
//   - "viewport space": CSS pixels inside the canvas element as currently
//     panned/zoomed. Pointer events arrive in this space and must be
//     converted via `viewportToImagePoint` before being used for anything
//     that touches image pixels.
const MAX_CANVAS_DIM = 1400; // cap canvas resolution for large source images (perf)
const MIN_ZOOM = 0.05;
const MAX_ZOOM = 8;
const BUTTON_ZOOM_FACTOR = 1.25; // +/- buttons step 25% per click
const PAN_MARGIN = 60; // px of the image always kept visible so it can't be lost off-screen

// MICRO-STEP 3 — mask layer (brush/eraser). The mask is authored fully opaque
// on an offscreen, image-space canvas (`maskDataRef`, created at `imgSize`
// resolution whenever a new image loads) so overlapping strokes never stack
// alpha on top of each other; MASK_DISPLAY_ALPHA is applied exactly once, at
// draw time, when that offscreen canvas is composited onto the visible IMAGE
// canvas — see `draw()` (MICRO-STEP 3.4 moved this there from `drawMask()`,
// which used to draw it onto a separate, topmost canvas that ended up
// visually tinting every paint color). This keeps the visual overlay
// semi-transparent and visually distinct from the image without ever
// touching the image canvas's underlying pixel data/original.
const MASK_PAINT_COLOR = "#ef4444"; // opaque authoring color on the offscreen mask canvas
const MASK_DISPLAY_ALPHA = 0.45; // display-time alpha of the mask overlay
const DEFAULT_BRUSH_SIZE = 32; // image-space px (diameter)
const MIN_BRUSH_SIZE = 4;
const MAX_BRUSH_SIZE = 240;
const BRUSH_STEP = 4;
// MICRO-STEP 3.2 — default selected color for Brush/"Riempi" color retouching.
// MICRO-STEP 3.3 (hardening, item H of the root-cause checklist): derived
// directly from `BG_PALETTE` itself (index 5, "#000000") instead of a
// separately-typed literal, so the initial "active" swatch ring can never
// point at a color that isn't actually a palette entry, even if the palette
// is ever reordered. Any palette entry works equally well as a default;
// black is a reasonable choice for covering small logos/watermarks, the most
// common case per spec.
const DEFAULT_PAINT_COLOR = BG_PALETTE[5];

function clampBrushSize(v) {
  return Math.min(MAX_BRUSH_SIZE, Math.max(MIN_BRUSH_SIZE, v));
}

function clampZoom(z) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

// "Fit to viewport" scale for the given image/container sizes (image space -> viewport space).
function computeFitZoom(containerW, containerH, imgW, imgH) {
  if (!containerW || !containerH || !imgW || !imgH) return 1;
  return Math.min(containerW / imgW, containerH / imgH);
}

function centeredPan(containerW, containerH, imgW, imgH, zoom) {
  return {
    x: (containerW - imgW * zoom) / 2,
    y: (containerH - imgH * zoom) / 2,
  };
}

// Keeps at least PAN_MARGIN px of the image visible on every edge so the user
// can never drag it permanently out of the viewport (requirement: never
// "lose" the image).
function clampPan(pan, containerW, containerH, imgW, imgH, zoom) {
  if (!containerW || !containerH || !imgW || !imgH) return pan;
  const scaledW = imgW * zoom;
  const scaledH = imgH * zoom;
  const minX = PAN_MARGIN - scaledW;
  const maxX = containerW - PAN_MARGIN;
  const minY = PAN_MARGIN - scaledH;
  const maxY = containerH - PAN_MARGIN;
  return {
    x: Math.min(Math.max(minX, maxX), Math.max(Math.min(minX, maxX), pan.x)),
    y: Math.min(Math.max(minY, maxY), Math.max(Math.min(minY, maxY), pan.y)),
  };
}

// Viewport (canvas CSS-pixel) coordinates -> image-space coordinates, using
// the current pan/zoom transform. This is the exact transform a future
// brush/lasso/mask step needs to turn a pointer event into an image-space
// paint coordinate; it is already correct here even though nothing consumes
// it for painting yet.
function viewportToImagePoint(cssX, cssY, pan, zoom) {
  return { x: (cssX - pan.x) / zoom, y: (cssY - pan.y) / zoom };
}

// Keeps the image point currently under (anchorCssX, anchorCssY) stationary
// on screen while zooming — used by wheel-zoom and the +/- buttons.
function zoomAtPoint(prevZoom, nextZoom, anchorCssX, anchorCssY, pan) {
  const imgPoint = viewportToImagePoint(anchorCssX, anchorCssY, pan, prevZoom);
  return {
    x: anchorCssX - imgPoint.x * nextZoom,
    y: anchorCssY - imgPoint.y * nextZoom,
  };
}

// TEMP DIAGNOSTIC (micro-step 2.5) — read-only DOM introspection helper, used
// only to compare wrapRef against the canvas, its immediate DOM parent, and
// the DialogContent ancestor when `wrapRef` keeps measuring 0x0 while
// something is visibly rendered on screen. Does not read/alter any layout;
// safe to delete once the browser output confirms which element is at fault.
function describeEl(el) {
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return {
    tagName: el.tagName,
    className: el.className,
    clientWidth: el.clientWidth,
    clientHeight: el.clientHeight,
    offsetWidth: el.offsetWidth,
    offsetHeight: el.offsetHeight,
    rectWidth: rect.width,
    rectHeight: rect.height,
    rectTop: rect.top,
    rectLeft: rect.left,
  };
}

export function RetouchEditorModal({
  open,
  onOpenChange,
  imageUrl,
  mediaAssetId,
  productId,
  onSaved, // MICRO-STEP 3.8 — called with the new derived asset's url, only after a real save succeeds
}) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  // MICRO-STEP 3 — visible, viewport-space, topmost canvas that captures all
  // pointer events (Pan/Brush/Eraser). MICRO-STEP 3.4: it no longer draws the
  // mask overlay itself (that visual moved into `draw()`, see the comments
  // there and on `drawMask()` below) — `drawMask()` now only keeps this
  // element's size in sync with `containerSize` and keeps it blank/
  // transparent.
  const maskCanvasRef = useRef(null);
  // MICRO-STEP 3 — the actual mask DATA lives here, as an offscreen canvas in
  // IMAGE space (created at `imgSize` resolution in the image-load effect
  // below). Brush/eraser paint directly onto this canvas using image-space
  // coordinates (via the existing `viewportToImagePoint`), so zoom/pan can
  // never distort or misalign the mask — `draw()` (MICRO-STEP 3.4) composites
  // this same canvas through the identical pan/zoom transform used for the
  // image, so the two stay pixel-perfect aligned at any zoom/pan/resize.
  const maskDataRef = useRef(null);
  // MICRO-STEP 3.1 — the EDIT BUFFER: an offscreen canvas, also in IMAGE
  // space, at the same resolution as `maskDataRef`/`imgSize`. Seeded as a
  // straight, read-only copy of `imgElRef`'s pixels whenever a new image
  // loads (see the load effect below). "Rimuovi area" (below) is the only
  // thing that writes to it; the original `<img>` element/`imageUrl` are
  // never touched by it. MICRO-STEP 3.2: `draw()` now composites this
  // together with `paintLayerRef` (below) onto the visible canvas — this
  // buffer alone is no longer the full picture.
  const editBufferRef = useRef(null);
  // MICRO-STEP 3.2 — the PAINT LAYER: a fourth offscreen canvas, same IMAGE
  // space/resolution as the two above, drawn ON TOP of `editBufferRef` in
  // `draw()`. This is where color retouches actually live — Brush strokes the
  // selected palette color onto it, Eraser removes from it (never from
  // `editBufferRef`), and "Riempi" paints the selected color into it clipped
  // to the current mask. Keeping this separate from `editBufferRef` is what
  // lets Eraser undo a color retouch non-destructively: it reveals whatever
  // is underneath (the original, or an already-removed area) instead of
  // needing to reconstruct anything.
  const paintLayerRef = useRef(null);
  // MICRO-STEP 3 — last image-space point painted, used to stroke a
  // continuous segment from the previous pointermove to the current one
  // (instead of isolated dabs) so fast strokes don't leave gaps.
  const lastPaintPointRef = useRef({ x: 0, y: 0 });
  // MICRO-STEP 3.7 — history of COMMITTED brush strokes (never Eraser), each
  // `{ points, size, color }` in IMAGE-SPACE, so "Riempi" can later
  // reconstruct each stroke's own historical color instead of only ever using
  // whichever color happens to be selected at the moment "Riempi" itself is
  // clicked. Root cause (micro-step 3.6): `paintColorRef.current` is a single
  // global scalar with no per-area memory — painting nero/verde/arancio/rosso
  // strokes and THEN clicking "Riempi" once left no way to recover which
  // color belonged to which area except what was already permanently baked
  // into `paintLayerRef`'s own pixels. Reset alongside mask/editBuffer/
  // paintLayer whenever a new image loads (see the load effect below), so it
  // never leaks between different assets or re-opens of the same one.
  const paintStrokesRef = useRef([]);
  // Accumulates the points of the brush stroke currently being drawn (one
  // pointerdown -> pointermove* -> pointerup gesture), committed as a single
  // entry to `paintStrokesRef` on pointerup — never populated for Eraser, and
  // never a per-`pointermove` record.
  const currentStrokeRef = useRef(null);
  const wrapRef = useRef(null);
  // TEMP DIAGNOSTIC (micro-step 2.5) — identity check only, to log the actual
  // DialogContent DOM node next to wrapRef/canvas. Not read by any
  // layout/measurement logic.
  const dialogContentRef = useRef(null);
  const imgElRef = useRef(null);
  const dragCleanupRef = useRef(null);
  const spaceHeldRef = useRef(false);
  const didInitViewRef = useRef(false);
  const measureRafRef = useRef([]); // pending rAF ids from the resilient re-measure below

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [imgReady, setImgReady] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  // FIX (micro-step 2.8): `wrapRef` alone is a passive ref — reading
  // `wrapRef.current` inside an effect keyed on `open` assumed the DOM node
  // already existed the moment `open` became true. It doesn't: Radix mounts
  // DialogContent's children (via its internal Presence state machine) in a
  // *second*, later commit than the one where `open` flips to true, and
  // `open` itself never changes again in between — so the measurement effect
  // below had exactly one chance to run, and it ran before the node existed,
  // then was never re-armed. `wrapEl` is state (not just a ref), driven by a
  // callback ref, so React itself notifies us the instant the node is
  // actually attached, and the measurement effect can depend on that instead
  // of guessing.
  const [wrapEl, setWrapEl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [toolMode, setToolMode] = useState(null); // null | "pan" | "brush" | "eraser"
  const [spaceHeld, setSpaceHeld] = useState(false); // cursor styling only
  const [isPanning, setIsPanning] = useState(false);
  const [isPainting, setIsPainting] = useState(false); // cursor styling only
  const [brushSize, setBrushSize] = useState(DEFAULT_BRUSH_SIZE); // image-space px
  // MICRO-STEP 3.2 — currently selected palette color, shared by Brush's
  // direct color painting and "Riempi"'s mask-clipped color fill.
  const [paintColor, setPaintColor] = useState(DEFAULT_PAINT_COLOR);
  // MICRO-STEP 3.8 — "Salva" round-trip state: `busy` drives the disabled/
  // "Salvataggio..." UI, `error` is shown inline (and clears on the next
  // attempt) without ever touching/resetting mask/editBuffer/paintLayer, so a
  // failed save never loses the user's local edit.
  const [saveState, setSaveState] = useState({ busy: false, error: null });
  // Ref mirror (not just the `busy` state above) so a fast double click is
  // rejected synchronously inside the same event-handler invocation, before
  // React has had a chance to re-render with the disabled button — a plain
  // state check alone could still race two rapid clicks into two uploads.
  const savingRef = useRef(false);

  // Refs mirroring the latest state, so imperative listeners (wheel/pointer/
  // keyboard, added once per modal-open lifecycle) always read fresh values
  // without needing to be re-bound on every state change — this is what
  // avoids duplicate listeners and stale-closure bugs.
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const panRef = useRef(pan);
  panRef.current = pan;
  const imgSizeRef = useRef(imgSize);
  imgSizeRef.current = imgSize;
  const containerSizeRef = useRef(containerSize);
  containerSizeRef.current = containerSize;
  const toolModeRef = useRef(toolMode);
  toolModeRef.current = toolMode;
  const imgReadyRef = useRef(imgReady);
  imgReadyRef.current = imgReady;
  const brushSizeRef = useRef(brushSize);
  brushSizeRef.current = brushSize;
  const paintColorRef = useRef(paintColor);
  paintColorRef.current = paintColor;

  const resetView = useCallback(() => {
    const { w: cw, h: ch } = containerSizeRef.current;
    const { w: iw, h: ih } = imgSizeRef.current;
    if (!cw || !ch || !iw || !ih) return;
    const fit = clampZoom(computeFitZoom(cw, ch, iw, ih));
    setZoom(fit);
    setPan(centeredPan(cw, ch, iw, ih, fit));
  }, []);

  const zoomBy = useCallback((factor) => {
    const { w: cw, h: ch } = containerSizeRef.current;
    const { w: iw, h: ih } = imgSizeRef.current;
    if (!cw || !ch || !iw || !ih) return;
    const prevZoom = zoomRef.current;
    const nextZoom = clampZoom(prevZoom * factor);
    if (nextZoom === prevZoom) return;
    const nextPan = zoomAtPoint(prevZoom, nextZoom, cw / 2, ch / 2, panRef.current);
    setZoom(nextZoom);
    setPan(clampPan(nextPan, cw, ch, iw, ih, nextZoom));
  }, []);

  // FIX (micro-step 2.8): memoized with `useCallback([])` so its identity is
  // stable across re-renders. An inline `ref={(node) => {...}}` would get a
  // *new* function on every render, and React calls the *previous* ref
  // callback with `null` then the *new* one with the node whenever the ref
  // prop's identity changes — even if the underlying DOM node didn't — which
  // would re-fire `setWrapEl(null)` then `setWrapEl(node)` on every render and
  // needlessly tear down/re-create the ResizeObserver + rAF pair in the
  // measurement effect below. A stable identity means React only invokes this
  // on real mount/unmount, exactly once per modal open/close.
  const setWrapNode = useCallback((node) => {
    wrapRef.current = node;
    setWrapEl(node);
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    // MICRO-STEP 3.1: paint the EDIT BUFFER, not the raw `imgElRef` image
    // element — this is what lets "Rimuovi area"/"Riempi" actually show up in
    // the preview. The buffer is seeded as an exact copy of the original at
    // load time (see the load effect below), so this is visually identical
    // to before until the user performs a local edit.
    const img = editBufferRef.current;
    const { w: cw, h: ch } = containerSizeRef.current;
    if (!canvas || !img || !cw || !ch) {
      // TEMP DIAGNOSTIC (micro-step 2.3) — remove once the browser confirms
      // the root cause. If this ever logs after the image has loaded, `cw`/
      // `ch` (containerSize) is the reason nothing gets painted.
      // eslint-disable-next-line no-console
      console.debug("[retouch] draw() skipped", {
        hasCanvas: !!canvas, hasImg: !!img, cw, ch,
      });
      return;
    }
    const dpr = window.devicePixelRatio || 1;
    const pxW = Math.max(1, Math.round(cw * dpr));
    const pxH = Math.max(1, Math.round(ch * dpr));
    if (canvas.width !== pxW || canvas.height !== pxH) {
      canvas.width = pxW;
      canvas.height = pxH;
    }
    canvas.style.width = `${cw}px`;
    canvas.style.height = `${ch}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    const { w: iw, h: ih } = imgSizeRef.current;
    const z = zoomRef.current;
    const p = panRef.current;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(img, p.x, p.y, iw * z, ih * z);
    // MICRO-STEP 3.4 fix — root cause of "nero appare marrone" / colors
    // looking tinted or washed out: the semi-transparent red mask overlay
    // used to be drawn on a SEPARATE, topmost DOM canvas (`maskCanvasRef`,
    // stacked above this one purely so it could receive pointer events) — so
    // visually it always sat on top of EVERYTHING, including the actual
    // selected color on `paintLayerRef`, permanently washing every retouch
    // color toward red/brown at MASK_DISPLAY_ALPHA (45%) regardless of which
    // color was selected. On top of that, brush/eraser dragging only ever
    // called `drawMask()` (never this function) while painting, so the real
    // paint-layer color was not even re-rendered onto this canvas during a
    // stroke — only the red mask tint was visibly refreshing at all.
    //
    // Fix: composite the mask overlay HERE, sandwiched between the edit
    // buffer (just drawn above) and the paint layer (drawn right after this),
    // using the exact same image-space -> viewport transform already in use
    // for both — no new coordinate math. This makes the paint layer's true,
    // currently selected color always the topmost, unobscured thing the user
    // sees. `maskCanvasRef` itself stays exactly where it was in the DOM
    // (still topmost, still the pointer-capture target for Brush/Eraser/Pan —
    // none of that interaction code changed) but no longer draws anything
    // visible — see `drawMask()` below. `handlePointerDown` now calls THIS
    // function after every paint segment (not `drawMask()`), so the real
    // color refreshes live while dragging, not just the mask tint.
    const maskData = maskDataRef.current;
    if (maskData) {
      ctx.globalAlpha = MASK_DISPLAY_ALPHA;
      ctx.drawImage(maskData, p.x, p.y, iw * z, ih * z);
      ctx.globalAlpha = 1;
    }
    // MICRO-STEP 3.2 — composite the PAINT LAYER on top of the mask overlay,
    // through the exact same destination rect (same image-space -> viewport
    // transform), so color retouches stay pixel-perfect aligned with the
    // image underneath at any zoom/pan/resize. A no-op visually while the
    // layer is still blank.
    const paintLayer = paintLayerRef.current;
    if (paintLayer) {
      ctx.drawImage(paintLayer, p.x, p.y, iw * z, ih * z);
    }
    // TEMP DIAGNOSTIC (micro-step 2.3) — remove once the browser confirms the
    // root cause. Shows the exact destination rect drawImage() was called
    // with, plus the canvas's own backing-store/CSS size at that moment.
    // eslint-disable-next-line no-console
    console.debug("[retouch] drawImage called", {
      dx: p.x, dy: p.y, dWidth: iw * z, dHeight: ih * z,
      canvasWidthAttr: canvas.width, canvasHeightAttr: canvas.height,
      canvasStyleWidth: canvas.style.width, canvasStyleHeight: canvas.style.height,
      cw, ch, zoom: z,
    });
  }, []);

  // MICRO-STEP 3.4 — `maskCanvasRef` (the topmost DOM canvas, kept there
  // ONLY so it can receive pointer events for Brush/Eraser/Pan — none of
  // that interaction wiring changed) no longer draws the mask visually:
  // doing so meant the red mask tint always sat on top of the paint layer's
  // real color, at MASK_DISPLAY_ALPHA, for every color, every time — see the
  // detailed root-cause comment in `draw()` above. The mask is still
  // authored exactly as before, in image space, fully opaque, on the
  // offscreen `maskDataRef` canvas; `draw()` now composites THAT onto the
  // visible image canvas itself, sandwiched between the edit buffer and the
  // paint layer, which is the actual fix.
  //
  // This function is kept (same name, same call sites/triggers as before —
  // including being called from `handlePointerDown` after every paint
  // segment) so `maskCanvasRef` keeps tracking `containerSize` (backing-store
  // size AND CSS box size) exactly as it did previously: that sizing is what
  // `handlePointerDown`'s `canvas.getBoundingClientRect()` pointer-to-image-
  // space math still relies on. It just no longer paints any pixels beyond
  // clearing it, so the element stays fully transparent and never visually
  // competes with the paint layer/mask now drawn in `draw()`.
  const drawMask = useCallback(() => {
    const maskCanvas = maskCanvasRef.current;
    const { w: cw, h: ch } = containerSizeRef.current;
    if (!maskCanvas || !cw || !ch) return;
    const dpr = window.devicePixelRatio || 1;
    const pxW = Math.max(1, Math.round(cw * dpr));
    const pxH = Math.max(1, Math.round(ch * dpr));
    if (maskCanvas.width !== pxW || maskCanvas.height !== pxH) {
      maskCanvas.width = pxW;
      maskCanvas.height = pxH;
    }
    maskCanvas.style.width = `${cw}px`;
    maskCanvas.style.height = `${ch}px`;
    const ctx = maskCanvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
  }, []);

  // MICRO-STEP 3 (+3.2) — paint (brush) or erase a straight segment, in
  // IMAGE-SPACE coordinates. Stroking a line (round cap/join) from the
  // previous point to the current one — rather than dabbing only at the exact
  // pointer-event coordinates — keeps fast strokes continuous instead of
  // leaving gaps between sparse pointermove samples. A zero-length segment
  // (from === to, used on pointerdown before any movement) still renders as a
  // single round dot because of the round line cap.
  //
  // Writes to TWO offscreen canvases with identical stroke geometry (same
  // from/to points, same `brushSizeRef.current` width — no new coordinate
  // math is introduced by adding the second one):
  //   - `maskDataRef`, as before micro-step 3.2 — indicates the retouched
  //     area (used by "Rimuovi area"/"Riempi" as a stencil).
  //   - `paintLayerRef` (MICRO-STEP 3.2) — the actual color, using the
  //     currently selected palette color. Never `editBufferRef`/the original:
  //     Eraser's `destination-out` here can only ever remove a color retouch
  //     from this layer, revealing whatever is underneath.
  const paintSegment = useCallback((from, to, erase) => {
    const maskData = maskDataRef.current;
    const paintLayer = paintLayerRef.current;
    if (!maskData || !paintLayer) return;

    const maskCtx = maskData.getContext("2d");
    maskCtx.save();
    maskCtx.globalCompositeOperation = erase ? "destination-out" : "source-over";
    maskCtx.strokeStyle = MASK_PAINT_COLOR;
    maskCtx.lineWidth = brushSizeRef.current;
    maskCtx.lineCap = "round";
    maskCtx.lineJoin = "round";
    maskCtx.beginPath();
    maskCtx.moveTo(from.x, from.y);
    maskCtx.lineTo(to.x, to.y);
    maskCtx.stroke();
    maskCtx.restore();

    const layerCtx = paintLayer.getContext("2d");
    // MICRO-STEP 3.3 fix — root cause of "Pennello continua a usare il colore
    // precedente": a paint stroke was composited onto `paintLayerRef` with the
    // DEFAULT `source-over` operation only. Wherever that footprint already
    // held non-fully-opaque pixels (the antialiased rim of the round brush
    // cap/join itself, an overlap with a previous stroke, or residue left by
    // Eraser's own antialiased partial erase), `source-over` alpha-BLENDS the
    // new color with whatever was already there instead of replacing it,
    // which is exactly what can make a freshly painted stroke visibly retain
    // a trace of the previous color. Fix: for an actual paint stroke (not
    // Eraser — `erase` already does exactly what it should with
    // `destination-out` alone), first stroke the IDENTICAL geometry
    // (path/lineWidth/cap/join) with `destination-out` to fully clear this
    // stroke's own footprint on the paint layer, THEN stroke it again with
    // `source-over` in the selected color. Both passes share the exact same
    // antialiased coverage, so the clear removes precisely what the color
    // pass is about to (re)draw — never more, never less — guaranteeing a
    // deterministic overwrite instead of a blend.
    if (!erase) {
      layerCtx.save();
      layerCtx.globalCompositeOperation = "destination-out";
      layerCtx.lineWidth = brushSizeRef.current;
      layerCtx.lineCap = "round";
      layerCtx.lineJoin = "round";
      layerCtx.beginPath();
      layerCtx.moveTo(from.x, from.y);
      layerCtx.lineTo(to.x, to.y);
      layerCtx.stroke();
      layerCtx.restore();
    }
    layerCtx.save();
    layerCtx.globalCompositeOperation = erase ? "destination-out" : "source-over";
    layerCtx.strokeStyle = paintColorRef.current;
    layerCtx.lineWidth = brushSizeRef.current;
    layerCtx.lineCap = "round";
    layerCtx.lineJoin = "round";
    layerCtx.beginPath();
    layerCtx.moveTo(from.x, from.y);
    layerCtx.lineTo(to.x, to.y);
    layerCtx.stroke();
    layerCtx.restore();
  }, []);

  // Load the ORIGINAL image (read-only) whenever the modal opens for a given
  // asset. Resets the view state so a previously-open asset's zoom/pan never
  // leaks into a different one.
  useEffect(() => {
    if (!open || !imageUrl) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setImgReady(false);
    didInitViewRef.current = false;
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setToolMode(null);
    // MICRO-STEP 3.8 — a fresh load/re-open always starts from a clean save
    // state too (same lifecycle as mask/editBuffer/paintLayer/paintStrokes
    // below): a stale error from a previous asset must never bleed into this
    // one, and a save can never be "in flight" across two different loads.
    savingRef.current = false;
    setSaveState({ busy: false, error: null });

    const img = new Image();
    // MICRO-STEP 3.9 — FIX: the image is served from a different origin
    // (backend API_ORIGIN) than the frontend. Without `crossOrigin`, the
    // browser loads it in "no-cors" mode: `drawImage` onto a canvas still
    // works (that's why viewing/zoom/pan/mask/brush/fill all worked fine),
    // but the canvas becomes permanently "tainted" — any later pixel-read
    // operation (`toBlob`/`toDataURL`/`getImageData`) throws/resolves null.
    // `editBufferRef` is seeded from `img` via `drawImage` below, so once
    // tainted it propagates to every canvas composited from it, including
    // `handleSave`'s export canvas — `exportCanvas.toBlob()` then silently
    // resolves `null`, hit by `if (!blob) throw new Error("export_failed")`,
    // surfacing as the generic "Salvataggio non riuscito. Riprova." The fix
    // is to request the image in real CORS mode: the backend's global
    // CORSMiddleware (backend/server.py) already allows the frontend origin
    // with credentials for every route, including `/media/{id}/content`
    // (a public, token-guarded endpoint that needs no cookies), so a plain
    // anonymous CORS request is enough — no backend change required.
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      imgElRef.current = img;
      const scale = Math.min(1, MAX_CANVAS_DIM / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      // MICRO-STEP 3 — (re)create the offscreen, image-space mask canvas at
      // exactly this resolution, for this asset. A brand-new blank (fully
      // transparent) canvas here is what guarantees the mask is always empty
      // for a freshly opened/loaded image and never leaks between different
      // assets or re-opens of the same asset.
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = w;
      maskCanvas.height = h;
      maskDataRef.current = maskCanvas;
      // MICRO-STEP 3.1 — (re)create the EDIT BUFFER at the same resolution,
      // seeded as a straight copy of the just-loaded original. Recreated from
      // scratch on every load (same as the mask above), so re-opening the
      // modal — or opening a different asset — always starts from a clean,
      // unedited buffer; `imgElRef`/`imageUrl` themselves are never written
      // to anywhere in this component.
      const editCanvas = document.createElement("canvas");
      editCanvas.width = w;
      editCanvas.height = h;
      editCanvas.getContext("2d").drawImage(img, 0, 0, w, h);
      editBufferRef.current = editCanvas;
      // MICRO-STEP 3.2 — (re)create the PAINT LAYER at the same resolution,
      // blank/fully transparent, for this asset — same reasoning as the mask
      // above: a fresh, empty layer on every load/re-open so color retouches
      // never leak between different assets or re-opens of the same one.
      const paintCanvas = document.createElement("canvas");
      paintCanvas.width = w;
      paintCanvas.height = h;
      paintLayerRef.current = paintCanvas;
      // MICRO-STEP 3.7 — reset the brush-stroke history for this asset, same
      // reasoning/lifecycle as the three offscreen buffers above: a clean
      // slate on every load/re-open.
      paintStrokesRef.current = [];
      currentStrokeRef.current = null;
      // TEMP DIAGNOSTIC (micro-step 2.3) — remove once the browser confirms
      // the root cause. Deliberately does not log `imageUrl` (may carry a
      // signed-URL token).
      // eslint-disable-next-line no-console
      console.debug("[retouch] image onload", {
        naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight,
        computedImgSize: { w, h },
      });
      setImgSize({ w, h });
      setImgReady(true);
      setLoading(false);
    };
    img.onerror = () => {
      if (cancelled) return;
      setError(true);
      setLoading(false);
    };
    img.src = imageUrl;

    return () => {
      cancelled = true;
      imgElRef.current = null;
      maskDataRef.current = null;
      editBufferRef.current = null;
      paintLayerRef.current = null;
      paintStrokesRef.current = [];
      currentStrokeRef.current = null;
      savingRef.current = false;
    };
  }, [open, imageUrl]);

  // Track the canvas wrapper's CSS size so the view stays responsive to the
  // modal being resized (window resize, layout changes, etc).
  //
  // FIX (micro-step 2.8 — root cause identified in 2.7, confirmed by the
  // absence of the 2.5 diagnostic log ever firing): this effect used to be
  // keyed on `[open]` alone and read `wrapRef.current` (a plain ref) once per
  // run. Radix Dialog's `Presence` mounts DialogContent's children — the
  // `<div ref={...}>` measured here — in a commit that happens *after* the
  // one where `open` becomes `true` (its own internal state machine advances
  // via a `useLayoutEffect`, scheduling a further render). `open` does not
  // change again between those two commits, so the old effect had exactly
  // one chance to run — in the commit where the node did not exist yet — and
  // was never re-armed once the node actually appeared. No amount of
  // `requestAnimationFrame`/`ResizeObserver` scheduling from *inside* that
  // effect could fix this: the code past the `if (!el) return` line was
  // simply never reached.
  //
  // The fix: `wrapEl` is real React state, set by a callback ref on the div
  // below. React invokes that callback ref (and therefore `setWrapEl`) the
  // moment the node is actually attached — in whichever commit that turns
  // out to be — so this effect, now keyed on `wrapEl` instead of `open`,
  // reliably (re-)runs as soon as there is something to measure. `wrapRef`
  // is left in place (mirrored by the same callback ref) for the other,
  // pre-existing imperative reads elsewhere in this file (e.g. inside
  // `draw()`'s canvas sizing math is via containerSizeRef, not wrapRef, so no
  // other call site needed to change).
  //
  // The two-`requestAnimationFrame` re-measure and the ResizeObserver from
  // micro-step 2.3 are kept as-is: harmless now that the effect actually
  // runs, and still useful as a safety net plus the ongoing responsive-resize
  // mechanism respectively.
  useEffect(() => {
    if (!open || !wrapEl) return undefined;
    const el = wrapEl;
    const update = () => {
      const rect = el.getBoundingClientRect();
      // TEMP DIAGNOSTIC (micro-step 2.3) — remove once the browser confirms
      // the root cause.
      // eslint-disable-next-line no-console
      console.debug("[retouch] container rect measured", { w: rect.width, h: rect.height });
      // TEMP DIAGNOSTIC (micro-step 2.5) — same call sites as the measurement
      // above (initial sync call, both rAF re-measures, every ResizeObserver
      // callback), but comparing wrapRef against the canvas, wrapRef's own
      // immediate DOM parent, and the DialogContent ancestor. Read-only: does
      // not feed into containerSize/layout in any way.
      // eslint-disable-next-line no-console
      console.debug("[retouch] DOM diagnostic (micro-step 2.5)", {
        wrap: describeEl(el),
        canvas: describeEl(canvasRef.current),
        wrapParent: describeEl(el.parentElement),
        dialogContent: describeEl(dialogContentRef.current),
      });
      setContainerSize({ w: Math.round(rect.width), h: Math.round(rect.height) });
    };
    update();
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(update);
      measureRafRef.current.push(raf2);
    });
    measureRafRef.current = [raf1];
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      measureRafRef.current.forEach((id) => cancelAnimationFrame(id));
      measureRafRef.current = [];
      ro.disconnect();
    };
  }, [open, wrapEl]);

  // First time the image and the container are both ready, fit + center —
  // this is also what "Reset view" returns to. Later, if the container is
  // resized (modal/window resize) *after* that initial fit, keep the
  // existing zoom but re-clamp pan so the image can't end up parked outside
  // the new viewport.
  //
  // FIX (micro-step 2.2 — 2.1 did not actually fix the blank canvas): this
  // used to be TWO separate effects (first-time-fit, then a resize-re-clamp
  // effect gated on `didInitViewRef.current`). `didInitViewRef.current` is a
  // *ref*, mutated synchronously inside the first effect's body — so on the
  // very first load both effects still ran back-to-back in the *same*
  // commit's effect-flush: the fit effect called setZoom(fit) +
  // setPan(centered-for-fit) (queued, not yet applied to state), and the
  // second effect ran immediately after, in that same flush, re-clamping
  // that queued pan. The 2.1 fix made it read `zoomRef.current` instead of
  // the `zoom` closure, but `zoomRef.current` is *only* mirrored during
  // render — and no render had happened yet between the queued setZoom(fit)
  // and this second effect running, so zoomRef.current was JUST AS STALE
  // (still the initial `1`). Two effects writing `pan` for the same state
  // transition can never be made safe by switching which stale read they
  // use; the fix is to not have two competing effects in the first place.
  // Merging them into one removes the duplicate write: on the very first
  // pass we only call resetView() and stop (no re-clamp in the same pass);
  // on a *later*, separate pass triggered by an actual resize, zoomRef is
  // guaranteed fresh because it was mirrored during the render that started
  // that pass, which already reflects any earlier, already-committed zoom.
  useEffect(() => {
    if (!imgReady) return;
    const { w: cw, h: ch } = containerSize;
    const { w: iw, h: ih } = imgSize;
    if (!cw || !ch || !iw || !ih) return;
    if (!didInitViewRef.current) {
      didInitViewRef.current = true;
      resetView();
      return;
    }
    setPan((prev) => clampPan(prev, cw, ch, iw, ih, zoomRef.current));
  }, [imgReady, containerSize, imgSize, resetView]);

  // Redraw whenever the transform or viewport changes. `draw()` (MICRO-STEP
  // 3.4) composites edit buffer + mask overlay + paint layer together, and
  // `drawMask()` just keeps the topmost, pointer-capture-only canvas sized to
  // match — both share this exact trigger set, so they can never fall out of
  // sync with each other on zoom/pan/resize. Painting itself (brush/eraser
  // drag) does not change any of these dependencies, so `paintSegment` call
  // sites in `handlePointerDown` call `draw()` directly as well.
  useEffect(() => {
    if (!imgReady) return;
    draw();
    drawMask();
  }, [imgReady, zoom, pan, containerSize, draw, drawMask]);

  // Wheel = zoom, anchored on the cursor position. Bound imperatively
  // (addEventListener, not the React onWheel prop) with { passive: false }
  // because React attaches synthetic wheel listeners as passive, which would
  // silently ignore preventDefault() and let the page scroll instead of
  // zooming. Bound once per modal-open lifecycle (reads live values through
  // refs) so it never duplicates.
  //
  // MICRO-STEP 3 correction (two issues found while making this layering
  // change, fixed together since both are on this exact effect):
  //
  // 1) This must bind to the MASK canvas, not the image canvas. Since
  //    micro-step 3 stacks the mask canvas on top (it now captures all
  //    pointer interaction, including painting), it also became the element
  //    the browser actually dispatches wheel events to. `wheel` does not
  //    bubble between sibling elements, so a listener left on the
  //    (now-covered) image canvas would simply never fire again.
  //
  // 2) This effect is keyed on `[open]` alone and reads a plain ref
  //    (`maskCanvasRef.current`) exactly once per run — the SAME shape of bug
  //    identified and fixed for `wrapRef`/the measurement effect in
  //    micro-step 2.7/2.8: Radix's `Presence` mounts DialogContent's entire
  //    children subtree (header, toolbar, the wrap div AND both canvases
  //    inside it, all together) in a commit that happens *after* the one
  //    where `open` becomes `true`, and `open` never changes again in
  //    between — so a `[open]`-only effect has exactly one chance to run, and
  //    it runs before that subtree exists. It was never caught for this
  //    effect specifically because nobody happened to test the wheel gesture
  //    in a way that surfaced it, but the mechanism is identical. Depending
  //    on `wrapEl` (state, set by the callback ref once the subtree is
  //    actually attached) instead of `open` fixes it the same way it fixed
  //    the measurement effect — and `canvasRef`/`maskCanvasRef` become
  //    non-null in that very same commit as `wrapEl`, since they're mounted
  //    together as part of the same subtree.
  useEffect(() => {
    if (!open || !wrapEl) return undefined;
    const canvas = maskCanvasRef.current;
    if (!canvas) return undefined;

    const handleWheel = (e) => {
      if (!imgReadyRef.current) return;
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const cssX = e.clientX - rect.left;
      const cssY = e.clientY - rect.top;
      const prevZoom = zoomRef.current;
      const factor = Math.min(1.6, Math.max(0.625, Math.pow(1.0015, -e.deltaY)));
      const nextZoom = clampZoom(prevZoom * factor);
      if (nextZoom === prevZoom) return;
      const nextPan = zoomAtPoint(prevZoom, nextZoom, cssX, cssY, panRef.current);
      const { w: cw, h: ch } = containerSizeRef.current;
      const { w: iw, h: ih } = imgSizeRef.current;
      setZoom(nextZoom);
      setPan(clampPan(nextPan, cw, ch, iw, ih, nextZoom));
    };

    canvas.addEventListener("wheel", handleWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", handleWheel);
  }, [open, wrapEl]);

  // Space bar = temporary pan gesture, mirroring common editor conventions.
  // Ignored while the user is typing in a form field elsewhere in the modal.
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (e) => {
      if (e.code !== "Space" || e.repeat) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      spaceHeldRef.current = true;
      setSpaceHeld(true);
    };
    const handleKeyUp = (e) => {
      if (e.code !== "Space") return;
      spaceHeldRef.current = false;
      setSpaceHeld(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      spaceHeldRef.current = false;
      setSpaceHeld(false);
    };
  }, [open]);

  // Safety net: if the modal is closed (or the component unmounts) while a
  // drag-pan gesture is in flight, force-remove the window-level pointermove/
  // pointerup listeners it registered so nothing leaks.
  useEffect(() => () => { dragCleanupRef.current?.(); }, [open]);

  // MICRO-STEP 3: this now handles two mutually-exclusive drag gestures on
  // the SAME (mask, topmost) canvas element:
  //   - pan (Pan tool active, OR Space held — Space always wins even if
  //     Brush/Eraser is the selected tool, per spec: "Space + drag deve
  //     continuare a funzionare come pan globale")
  //   - paint (Brush/Eraser tool active, and pan is not requested)
  // The pan branch is byte-for-byte the same logic as before (micro-step 2);
  // only the pointer-capture target moved from the image canvas to the mask
  // canvas, since the mask canvas is now the topmost element actually
  // receiving pointer events. The window-level move/up listeners it installs
  // never touched the mask, so nothing about pan behavior changes.
  const handlePointerDown = (e) => {
    if (!imgReadyRef.current) return;
    const tool = toolModeRef.current;
    const canPan = tool === "pan" || spaceHeldRef.current;
    const isPaintTool = !canPan && (tool === "brush" || tool === "eraser");
    if (!canPan && !isPaintTool) return;
    e.preventDefault();
    const canvas = maskCanvasRef.current;
    canvas?.setPointerCapture?.(e.pointerId);

    if (canPan) {
      const startPan = panRef.current;
      const startX = e.clientX;
      const startY = e.clientY;
      setIsPanning(true);

      const handleMove = (ev) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        const { w: cw, h: ch } = containerSizeRef.current;
        const { w: iw, h: ih } = imgSizeRef.current;
        const z = zoomRef.current;
        const nextPan = { x: startPan.x + dx, y: startPan.y + dy };
        setPan(clampPan(nextPan, cw, ch, iw, ih, z));
      };
      const handleUp = () => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        window.removeEventListener("pointercancel", handleUp);
        dragCleanupRef.current = null;
        setIsPanning(false);
      };
      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
      window.addEventListener("pointercancel", handleUp);
      dragCleanupRef.current = handleUp;
      return;
    }

    // Paint (brush/eraser): convert pointer events to IMAGE-SPACE points via
    // the same `viewportToImagePoint` used by wheel-zoom/pan — no new
    // coordinate math is introduced.
    const erase = tool === "eraser";
    const rect = canvas.getBoundingClientRect();
    const toImagePoint = (clientX, clientY) => viewportToImagePoint(
      clientX - rect.left, clientY - rect.top, panRef.current, zoomRef.current,
    );
    // MICRO-STEP 3.4: redraw via `draw()`, not `drawMask()` — the mask
    // overlay's visual is now composited inside `draw()` itself (see the
    // comments there), so `draw()` is what needs to run after every paint
    // segment for the stroke (mask tint AND the real paint-layer color) to
    // actually refresh on screen while dragging.
    const startPt = toImagePoint(e.clientX, e.clientY);
    lastPaintPointRef.current = startPt;
    // MICRO-STEP 3.7 — start recording this gesture's own stroke history, in
    // IMAGE-SPACE, capturing the size/color active right now at its start
    // (Brush only — `erase` stays out of `paintStrokesRef` entirely, per
    // spec). Committed as ONE entry on pointerup below, not per-pointermove.
    currentStrokeRef.current = erase
      ? null
      : { points: [startPt], size: brushSizeRef.current, color: paintColorRef.current };
    paintSegment(startPt, startPt, erase); // dot on click-without-drag
    draw();
    setIsPainting(true);

    const handleMove = (ev) => {
      const pt = toImagePoint(ev.clientX, ev.clientY);
      paintSegment(lastPaintPointRef.current, pt, erase);
      lastPaintPointRef.current = pt;
      if (currentStrokeRef.current) currentStrokeRef.current.points.push(pt);
      draw();
    };
    const handleUp = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
      dragCleanupRef.current = null;
      setIsPainting(false);
      // MICRO-STEP 3.7 — commit the finished brush stroke (if any) as a
      // single history entry so "Riempi" can later reconstruct its color.
      if (currentStrokeRef.current) {
        paintStrokesRef.current.push(currentStrokeRef.current);
        currentStrokeRef.current = null;
      }
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    dragCleanupRef.current = handleUp;
  };

  // MICRO-STEP 3.1 — "Rimuovi area": punch a transparent hole into the EDIT
  // BUFFER wherever the current mask is painted. `destination-out` composited
  // with the mask canvas as source erases buffer pixels in proportion to the
  // mask's own alpha at that pixel (fully opaque brush strokes erase fully;
  // the antialiased edge of a stroke partially erases, which is what gives a
  // soft edge instead of a hard-pixelated one). Both canvases are already the
  // same IMAGE-SPACE resolution (`imgSize`), so no transform/scaling is
  // needed here — unlike `draw()`/`drawMask()`, this never touches zoom/pan.
  // A no-op (harmless) if the mask is currently empty. Redraws immediately so
  // the removed area is visible right away, per spec.
  const handleRemoveArea = () => {
    const editBuf = editBufferRef.current;
    const maskData = maskDataRef.current;
    if (!editBuf || !maskData || !imgReadyRef.current) return;
    const ctx = editBuf.getContext("2d");
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    ctx.drawImage(maskData, 0, 0);
    ctx.restore();
    draw();
  };

  // MICRO-STEP 3.2 — "Riempi" REDEFINED per this micro-step's spec: it now
  // paints the currently SELECTED COLOR into the area indicated by the mask,
  // instead of 3.1's neighbor-pixel reconstruction (which is removed — this
  // spec explicitly asks for "mask -> area selezionata -> colore corrente ->
  // riempimento locale", a different operation, not an extension of it).
  //
  // Only the pixels actually covered by the mask are filled ("se tecnicamente
  // più corretto, riempire solo i pixel coperti dalla mask", per spec): a
  // same-size scratch canvas is filled solid with the selected color, then
  // clipped to the mask's own alpha via `destination-in` (this also carries
  // over the mask's soft, antialiased edge — same proportional-alpha
  // treatment `handleRemoveArea` above already relies on). The result is
  // composited onto the PAINT LAYER (never `editBufferRef`/the original), so
  // Eraser can undo a "Riempi" the same way it undoes a brushed color stroke.
  // No zoom/pan/canvas viewport state is read here, so the result cannot
  // depend on the current view.
  //
  // MICRO-STEP 3.5 — the compositing of that patch onto the paint layer (see
  // the comment right above the fallback `destination-over` call below) now
  // ONLY ever fills pixels within the mask that are still transparent on the
  // paint layer; anything already painted there (a previous brush stroke, or
  // an earlier "Riempi" pass with a different color) is left exactly as-is,
  // never repainted — mask stays the STENCIL for "where", the paint layer's
  // own existing alpha decides "whether", never the other way around.
  //
  // MICRO-STEP 3.7 — root cause found in 3.6: with MANY brush strokes of
  // DIFFERENT colors painted before a single "Riempi" click, 3.5's fix (a
  // single patch in whatever color happens to be selected RIGHT NOW,
  // destination-over onto the paint layer) still only ever protects pixels
  // that are ALREADY fully opaque. Any pixel inside the mask that is not yet
  // fully opaque — a stroke's own antialiased rim, a small gap left by an
  // imperfect pass — gets "completed" with today's globally selected color,
  // which is only ever coincidentally correct for whichever area happens to
  // have been painted LAST. `paintColorRef.current` is a single scalar with
  // no memory of "which color belonged to which area" — that association was
  // never recorded anywhere except, irreversibly, in the paint layer's own
  // already-opaque pixels.
  //
  // Fix: before falling back to that single current-color patch, replay
  // `paintStrokesRef` (MICRO-STEP 3.7, populated in `handlePointerDown`) in
  // chronological order. For each recorded stroke, rebuild the EXACT same
  // footprint it originally painted (same points/lineWidth/cap/join as
  // `paintSegment` uses — no bounding-box approximation), clip it to the
  // CURRENT mask (a stroke's mask contribution may since have been partially
  // erased), and composite it onto the paint layer with the same
  // non-destructive `destination-over` used below — so it can only ever
  // complete that stroke's OWN still-transparent pixels with that stroke's
  // OWN historical color, never touch anything already opaque (from this or
  // any other stroke). Only after every recorded stroke has had a chance to
  // reclaim its own area does the original current-color patch run, as a
  // last-resort fallback for whatever (if anything) is still transparent
  // inside the mask and was not covered by any recorded stroke's footprint.
  const handleFill = () => {
    const maskData = maskDataRef.current;
    const paintLayer = paintLayerRef.current;
    const { w, h } = imgSizeRef.current;
    if (!maskData || !paintLayer || !w || !h || !imgReadyRef.current) return;

    // No-op guard: nothing to fill if the mask is currently empty.
    const maskPixels = maskData.getContext("2d").getImageData(0, 0, w, h).data;
    let hasMask = false;
    for (let i = 3; i < maskPixels.length; i += 4) {
      if (maskPixels[i] > 0) { hasMask = true; break; }
    }
    if (!hasMask) return;

    const layerCtx = paintLayer.getContext("2d");

    // MICRO-STEP 3.7 — replay each historical stroke's OWN color into its OWN
    // footprint first (chronological order), before the current-color
    // fallback below ever runs.
    paintStrokesRef.current.forEach((stroke) => {
      if (!stroke.points || stroke.points.length === 0) return;
      const stencil = document.createElement("canvas");
      stencil.width = w;
      stencil.height = h;
      const sctx = stencil.getContext("2d");
      sctx.strokeStyle = stroke.color;
      sctx.lineWidth = stroke.size;
      sctx.lineCap = "round";
      sctx.lineJoin = "round";
      sctx.beginPath();
      sctx.moveTo(stroke.points[0].x, stroke.points[0].y);
      if (stroke.points.length === 1) {
        sctx.lineTo(stroke.points[0].x, stroke.points[0].y); // dot, same as paintSegment
      } else {
        for (let i = 1; i < stroke.points.length; i += 1) {
          sctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }
      }
      sctx.stroke();
      // Clip this stroke's rebuilt footprint to the CURRENT mask (it may have
      // been partially erased since this stroke was originally painted).
      sctx.globalCompositeOperation = "destination-in";
      sctx.drawImage(maskData, 0, 0);

      layerCtx.save();
      layerCtx.globalCompositeOperation = "destination-over";
      layerCtx.drawImage(stencil, 0, 0);
      layerCtx.restore();
    });

    const patch = document.createElement("canvas");
    patch.width = w;
    patch.height = h;
    const patchCtx = patch.getContext("2d");
    patchCtx.fillStyle = paintColorRef.current;
    patchCtx.fillRect(0, 0, w, h);
    patchCtx.globalCompositeOperation = "destination-in";
    patchCtx.drawImage(maskData, 0, 0);

    // MICRO-STEP 3.5 fix — root cause of "Riempi ridipinge TUTTA l'area
    // mascherata, cancellando colori già esistenti": the 3.3 fix above (now
    // replaced) cleared the ENTIRE masked region on the paint layer first
    // (`destination-out` using the whole mask as the stencil), THEN drew the
    // new patch with `source-over`. That clear didn't distinguish "already
    // painted" from "still transparent" within the mask — it wiped out any
    // earlier brush stroke or "Riempi" pass in that area indiscriminately
    // before the new color landed, so a mask covering two differently-colored
    // regions (or a second "Riempi" with a new color on the same mask) turned
    // the WHOLE area into the newest color instead of only filling the
    // untouched part.
    //
    // Fix: composite the (already mask-clipped) patch with `destination-over`
    // instead, with NO preceding clear. Per Porter-Duff `destination-over`
    // (Co = Cs*(1-Da) + Cd*Da, Ao = Sa*(1-Da) + Da): wherever the paint layer
    // is already fully opaque (Da=1, existing paint — including what the
    // stroke-history replay above just reconstructed), the result is exactly
    // Cd — the OLD color, untouched, this patch is fully ignored there.
    // Wherever the paint layer is fully transparent (Da=0, never painted, and
    // not covered by any recorded stroke's footprint either), the result is
    // exactly Cs — the NEW patch color, i.e. a real fill. Wherever it's
    // partially transparent (an antialiased mask/stroke edge), the two blend
    // proportionally — the same soft-edge treatment already used elsewhere in
    // this file (`handleRemoveArea`, the patch's own `destination-in` clip
    // above), not a hard cutoff. This makes "Riempi" non-destructive by
    // construction: it can only ever add color where there was none, never
    // repaint what's already there.
    layerCtx.save();
    layerCtx.globalCompositeOperation = "destination-over";
    layerCtx.drawImage(patch, 0, 0);
    layerCtx.restore();
    draw();
  };

  // MICRO-STEP 3.8 — "Salva": materialize the current local result as a new
  // DERIVED product asset, through the EXISTING Media Engine + a new, minimal
  // linking endpoint (see backend/app/routers/product_hunter.py
  // `save_retouch_image`) — no new storage system, no AI/provider involved.
  //
  // EXPORT (the part the spec calls out as IMPORTANTISSIMO): a fresh,
  // image-space canvas (same `w`/`h` as `imgSizeRef.current` — never the
  // visible canvas's viewport/zoom/pan/DPR-scaled size, so this is never a
  // "screenshot of the modal") is composed from EXACTLY `editBufferRef` +
  // `paintLayerRef`, with plain default `source-over` and no third layer —
  // i.e. deliberately NOT calling `draw()`/reading `canvasRef`, since that
  // visible canvas also contains the mask overlay at MASK_DISPLAY_ALPHA
  // (see `draw()` above) and would bake the mask's red tint into the saved
  // file. `maskDataRef`/`maskCanvasRef` and any cursor/UI are never part of
  // this export.
  //
  // UPLOAD + LINK: the exported PNG blob is uploaded via `POST /media` —
  // byte-for-byte the same endpoint/FormData shape `Media.jsx` already uses,
  // no new upload mechanism — then the returned `media_asset_id` is linked to
  // this product as a derived asset via the new `save-retouch-image`
  // endpoint, which validates the product/source belong to this org (via
  // TenantRepo, same as every other Product Hunter endpoint) and never
  // touches the immutable original.
  //
  // SUCCESS/ERROR per spec: `onSaved(url)` is called and the modal closes
  // ONLY once BOTH network calls have actually succeeded. On ANY failure
  // (export/upload/link), the modal stays open, no local state (mask/edit
  // buffer/paint layer/history) is reset, and `saveState.error` is shown so
  // the user can simply click "Salva" again — a real retry, not a reload.
  // `savingRef` (checked synchronously first, before any `await`) makes a
  // double click produce at most one upload/one derived asset.
  const handleSave = useCallback(async () => {
    if (savingRef.current) return;
    if (!imgReadyRef.current || !mediaAssetId || !productId) return;
    const editBuf = editBufferRef.current;
    const paintLayer = paintLayerRef.current;
    const { w, h } = imgSizeRef.current;
    if (!editBuf || !paintLayer || !w || !h) return;

    savingRef.current = true;
    setSaveState({ busy: true, error: null });
    try {
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = w;
      exportCanvas.height = h;
      const exportCtx = exportCanvas.getContext("2d");
      exportCtx.drawImage(editBuf, 0, 0);
      exportCtx.drawImage(paintLayer, 0, 0);

      const blob = await new Promise((resolve) => exportCanvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("export_failed");

      const fd = new FormData();
      fd.append("file", blob, `retouch_${mediaAssetId}.png`);
      const uploadRes = await api.post("/media", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const uploadedId = uploadRes.data?.id;
      if (!uploadedId) throw new Error("upload_failed");

      const saveRes = await api.post(`/product-hunter/products/${productId}/save-retouch-image`, {
        source_media_asset_id: mediaAssetId,
        media_asset_id: uploadedId,
      });
      if (saveRes.data?.status !== "succeeded" || !saveRes.data?.url) {
        throw new Error("save_failed");
      }

      savingRef.current = false;
      setSaveState({ busy: false, error: null });
      // Pass the derived asset's own media_asset_id along with the url: the parent
      // (ProductHunter.jsx) needs it to keep the ORIGINALE/RISULTATO source selector usable
      // on the just-saved result too (result -> "Risultato" can itself become a future source).
      onSaved?.(saveRes.data.url, saveRes.data.media_asset_id);
      onOpenChange(false);
    } catch (err) {
      savingRef.current = false;
      const detail = err?.response?.data?.detail;
      const msg = (typeof detail === "string" && detail) || t("ph.retouchSaveError");
      setSaveState({ busy: false, error: msg });
    }
  }, [mediaAssetId, productId, onSaved, onOpenChange, t]);

  const canPanNow = toolMode === "pan" || spaceHeld;
  const zoomPct = imgReady ? Math.round(zoom * 100) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={dialogContentRef}
        className="max-w-3xl max-h-[88vh] overflow-y-auto rounded-sm"
        data-testid={`ph-retouch-dialog-${mediaAssetId || "none"}`}
        data-product-id={productId || undefined}>
        <DialogHeader>
          <DialogTitle>{t("ph.retouchTitle")}</DialogTitle>
          <DialogDescription>{t("ph.retouchDescription")}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="ph-retouch-toolbar">
          <div className="flex items-center gap-1 flex-wrap">
            <Button type="button" size="sm" variant={toolMode === "pan" ? "default" : "outline"}
              className="rounded-sm h-7 w-7 p-0" disabled={!imgReady}
              onClick={() => setToolMode((m) => (m === "pan" ? null : "pan"))}
              title={t("ph.retouchPanTool")} data-testid="ph-retouch-tool-pan">
              <Hand size={14} />
            </Button>
            {/* MICRO-STEP 3 — Brush/Eraser tools. Mutually exclusive with each
                other and with Pan (selecting one clears the others), same
                toggle pattern as the Pan button above. */}
            <Button type="button" size="sm" variant={toolMode === "brush" ? "default" : "outline"}
              className="rounded-sm h-7 w-7 p-0" disabled={!imgReady}
              onClick={() => setToolMode((m) => (m === "brush" ? null : "brush"))}
              title={t("ph.retouchBrushTool")} data-testid="ph-retouch-tool-brush">
              <PaintBrush size={14} />
            </Button>
            <Button type="button" size="sm" variant={toolMode === "eraser" ? "default" : "outline"}
              className="rounded-sm h-7 w-7 p-0" disabled={!imgReady}
              onClick={() => setToolMode((m) => (m === "eraser" ? null : "eraser"))}
              title={t("ph.retouchEraserTool")} data-testid="ph-retouch-tool-eraser">
              <Eraser size={14} />
            </Button>
            <span className="w-px h-5 bg-border mx-1" aria-hidden="true" />
            {/* MICRO-STEP 3 — brush size, in IMAGE-SPACE px (shared by Brush
                and Eraser). Clamped, and deliberately does NOT depend on the
                current zoom level. */}
            <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 w-7 p-0"
              disabled={!imgReady} onClick={() => setBrushSize((s) => clampBrushSize(s - BRUSH_STEP))}
              title={t("ph.retouchBrushSmaller")} data-testid="ph-retouch-brush-smaller">
              <Minus size={12} />
            </Button>
            <span className="text-xs tabular-nums w-11 text-center text-muted-foreground"
              data-testid="ph-retouch-brush-size-value">
              {brushSize}px
            </span>
            <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 w-7 p-0"
              disabled={!imgReady} onClick={() => setBrushSize((s) => clampBrushSize(s + BRUSH_STEP))}
              title={t("ph.retouchBrushLarger")} data-testid="ph-retouch-brush-larger">
              <Plus size={12} />
            </Button>
            <span className="w-px h-5 bg-border mx-1" aria-hidden="true" />
            {/* MICRO-STEP 3.1 — "Rimuovi area"/"Riempi": one-shot actions (like
                "Reimposta vista"), not persistent tool modes — they run once
                on click and don't participate in the Pan/Brush/Eraser
                mutual-exclusion above. */}
            <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs px-2"
              disabled={!imgReady} onClick={handleRemoveArea}
              title={t("ph.retouchRemoveArea")} data-testid="ph-retouch-remove-area">
              <Scissors size={13} className="mr-1" />{t("ph.retouchRemoveArea")}
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs px-2"
              disabled={!imgReady} onClick={handleFill}
              title={t("ph.retouchFill")} data-testid="ph-retouch-fill">
              <PaintBucket size={13} className="mr-1" />{t("ph.retouchFill")}
            </Button>
            <span className="w-px h-5 bg-border mx-1" aria-hidden="true" />
            {/* MICRO-STEP 3.8 — "Salva": one-shot action like "Rimuovi area"/
                "Riempi" above (not a persistent tool mode). Disabled while a
                save is already in flight (`saveState.busy`, backed by the
                synchronous `savingRef` guard inside `handleSave`), which is
                what makes a double click produce at most one derived asset. */}
            <Button type="button" size="sm" className="rounded-sm h-7 text-xs px-2"
              disabled={!imgReady || saveState.busy} onClick={handleSave}
              title={t("ph.retouchSave")} data-testid="ph-retouch-save">
              {saveState.busy
                ? <><Spinner size={13} className="animate-spin mr-1" />{t("ph.retouchSaving")}</>
                : <><FloppyDisk size={13} className="mr-1" />{t("ph.retouchSave")}</>}
            </Button>
          </div>

          <div className="flex items-center gap-1">
            <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 w-7 p-0"
              disabled={!imgReady} onClick={() => zoomBy(1 / BUTTON_ZOOM_FACTOR)}
              title={t("ph.retouchZoomOut")} data-testid="ph-retouch-zoom-out">
              <MagnifyingGlassMinus size={14} />
            </Button>
            <span className="text-xs tabular-nums w-12 text-center text-muted-foreground"
              data-testid="ph-retouch-zoom-level">
              {zoomPct !== null ? `${zoomPct}%` : "—"}
            </span>
            <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 w-7 p-0"
              disabled={!imgReady} onClick={() => zoomBy(BUTTON_ZOOM_FACTOR)}
              title={t("ph.retouchZoomIn")} data-testid="ph-retouch-zoom-in">
              <MagnifyingGlassPlus size={14} />
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs px-2 ml-1"
              disabled={!imgReady} onClick={resetView}
              title={t("ph.retouchResetView")} data-testid="ph-retouch-zoom-reset">
              <ArrowsClockwise size={13} className="mr-1" />{t("ph.retouchResetView")}
            </Button>
          </div>
        </div>

        {/* MICRO-STEP 3.8 — inline "Salva" error: shown only after a failed
            save attempt, never resets any local edit state. The user retries
            simply by clicking "Salva" again (same button, no separate retry
            control needed — it is not disabled by this error). */}
        {saveState.error && (
          <div className="flex items-center gap-1.5 text-[11px] text-destructive"
            data-testid="ph-retouch-save-error">
            <Info size={13} />{saveState.error}
          </div>
        )}

        {/* MICRO-STEP 3.2 — "Colore": SAME swatches, same order, same visual
            style (rounded-full swatch buttons, ring-2 on the active one) as
            the Background Editor's palette (`ph-bg-palette`/`ph-bg-swatch-*`
            in ProductHunter.jsx) — imported from the shared `BG_PALETTE`
            constant rather than redefined here. Selects the color Brush
            paints and "Riempi" fills with; purely local UI state, no
            upload/backend call. */}
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="ph-retouch-palette">
          <span className="text-[11px] text-muted-foreground mr-0.5">{t("ph.retouchColorLabel")}</span>
          {BG_PALETTE.map((hex) => {
            const active = paintColor.toUpperCase() === hex;
            return (
              <button key={hex} type="button" title={hex} aria-label={hex}
                aria-pressed={active}
                onClick={() => setPaintColor(hex)}
                className={`h-6 w-6 rounded-full border transition-transform hover:scale-110 ${active ? "ring-2 ring-primary ring-offset-1 ring-offset-background border-primary" : "border-border"}`}
                style={{ backgroundColor: hex }}
                data-testid={`ph-retouch-swatch-${hex.replace("#", "").toLowerCase()}`} />
            );
          })}
        </div>

        <div
          // FIX (micro-step 2.8): callback ref instead of a plain `ref={wrapRef}`
          // — mirrors the node into `wrapRef` (for any existing imperative
          // reads) AND into `wrapEl` state, so the measurement effect above is
          // notified (and re-runs) the instant Radix actually attaches this
          // node, instead of only once when `open` changes. `node` is `null`
          // on unmount, which correctly clears `wrapEl` too.
          ref={setWrapNode}
          // FIX (micro-step 2.4): the previous `h-[65vh]` relied on a viewport
          // unit to size a CSS-grid row whose only children are all
          // `position:absolute` (loading/error/canvas) — i.e. nothing in-flow
          // ever contributes a fallback height, and this exact combination
          // (vh height + grid row + all-absolute children, DialogContent with
          // no height constraint of its own) is not used anywhere else in
          // this file. Every OTHER working image dialog here (ph-bg-editor,
          // ph-detail, ph-listing) instead pairs a `max-h-[..vh] overflow-y-auto`
          // on DialogContent with a *fixed* px/rem height on the image
          // container — never `vh` on the inner element. Mirroring that proven
          // pattern (fixed, breakpoint-based height here; `max-h-[88vh]
          // overflow-y-auto` added to DialogContent above) removes the
          // dependency that produced a persistent 0x0 measurement.
          className="relative w-full h-[360px] sm:h-[440px] lg:h-[520px] max-h-[640px] min-h-[320px] overflow-hidden border border-border rounded-sm bg-muted/30"
          data-testid="ph-retouch-canvas-wrap">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground"
              data-testid="ph-retouch-loading">
              <Spinner size={22} className="animate-spin mr-2" />{t("ph.loading")}
            </div>
          )}
          {!loading && error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground"
              data-testid="ph-retouch-error">
              <Info size={22} className="text-destructive" />
              {t("ph.retouchLoadError")}
            </div>
          )}
          {/* Draw order (bottom to top), as of MICRO-STEP 3.4: edit buffer ->
              mask overlay -> paint layer, ALL composited together onto THIS
              canvas by `draw()` -> pointer/cursor interaction (the second
              canvas below). Previously the mask overlay was drawn on the
              second canvas instead, which — being topmost — visually tinted
              every paint color red/brown; see the root-cause comment on
              `draw()`. This canvas is purely read-only display: it never
              receives pointer events (the second canvas, same absolute
              positioning, is the topmost element and captures all pointer
              interaction for pan-drag/paint-drag, but no longer draws
              anything visible itself — see `drawMask()`). */}
          <canvas ref={canvasRef}
            className={`absolute left-0 top-0 ${loading || error ? "hidden" : "block"}`}
            data-testid="ph-retouch-canvas" />
          {/* MICRO-STEP 3 (+3.4) — topmost, pointer-capture-only canvas: kept
              sized/cleared by `drawMask()` so `getBoundingClientRect()` stays
              correct for pointer-to-image-space math, but intentionally
              paints nothing visible anymore — the mask overlay itself is now
              composited inside `draw()` on the canvas above, sandwiched
              between the edit buffer and the paint layer. */}
          <canvas ref={maskCanvasRef}
            onPointerDown={handlePointerDown}
            style={{ touchAction: "none" }}
            className={`absolute left-0 top-0 ${loading || error ? "hidden" : "block"} ${
              canPanNow
                ? (isPanning ? "cursor-grabbing" : "cursor-grab")
                // MICRO-STEP 3.2 — Pennello and Gomma now get visually
                // distinct cursors (previously both shared "crosshair").
                // `cursor-cell` is the closest built-in Tailwind/CSS cursor
                // keyword to an "eraser" cursor without introducing a custom
                // cursor image asset (none exists in this codebase/no new
                // libraries allowed).
                : toolMode === "brush"
                  ? "cursor-crosshair"
                  : (toolMode === "eraser" ? "cursor-cell" : "cursor-default")
            }`}
            data-testid="ph-retouch-mask-canvas"
            data-painting={isPainting ? "true" : undefined} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
